import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";

function loadImage(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}
function drawCover(ctx, src, w, h) {
  const sw = src.videoWidth || src.width, sh = src.videoHeight || src.height;
  if (!sw || !sh) return;
  const r = Math.max(w / sw, h / sh);
  const nw = sw * r, nh = sh * r;
  ctx.drawImage(src, (w - nw) / 2, (h - nh) / 2, nw, nh);
}
function drawContain(ctx, src, w, h) {
  const sw = src.videoWidth || src.width, sh = src.videoHeight || src.height;
  if (!sw || !sh) return;
  const r = Math.min(w / sw, h / sh);
  const nw = sw * r, nh = sh * r;
  ctx.drawImage(src, (w - nw) / 2, (h - nh) / 2, nw, nh);
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Composites the local camera with an optional background effect:
//   "none"  – raw camera
//   "blur"  – camera with the room blurred behind the person
//   "image" – person over the Fieldlink branded background
// When screen sharing, the shared screen fills the frame and the person is
// placed in the bottom-right corner. Always falls back to the raw camera frame
// until (or if) segmentation produces a mask, so the preview is never black.
export class VirtualBg {
  constructor({ width = 1280, height = 720, bgType = "image" } = {}) {
    this.w = width; this.h = height;
    this.bgType = bgType;
    this.canvas = document.createElement("canvas");
    this.canvas.width = this.w; this.canvas.height = this.h;
    this.ctx = this.canvas.getContext("2d");
    this.person = document.createElement("canvas");
    this.person.width = this.w; this.person.height = this.h;
    this.pctx = this.person.getContext("2d");
    this.mode = "camera";
    this.running = false;
    this.hasResult = false;
    this._raf = null;
  }

  async init(cameraStream, bgUrl) {
    this.camVideo = document.createElement("video");
    this.camVideo.muted = true; this.camVideo.playsInline = true;
    this.camVideo.srcObject = cameraStream;
    await this.camVideo.play().catch(() => {});
    try { this.bgImg = await loadImage(bgUrl); } catch { this.bgImg = null; }
    try {
      this.seg = new SelfieSegmentation({
        locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1.1675465747/${f}`,
      });
      this.seg.setOptions({ modelSelection: 1, selfieMode: false });
      this.seg.onResults((r) => this._onSeg(r));
    } catch { this.seg = null; }
    this.running = true;
    this._loop();
    return this.canvas.captureStream(30);
  }

  setBgType(t) { this.bgType = t; if (t === "none") this.hasResult = false; }

  _onSeg(results) {
    const c = this.pctx, w = this.w, h = this.h;
    c.save();
    c.clearRect(0, 0, w, h);
    c.drawImage(results.segmentationMask, 0, 0, w, h);
    c.globalCompositeOperation = "source-in";
    c.drawImage(results.image, 0, 0, w, h);
    c.globalCompositeOperation = "destination-over";
    if (this.bgType === "blur") {
      c.filter = "blur(12px)";
      drawCover(c, results.image, w, h);
      c.filter = "none";
    } else if (this.bgImg) {
      drawCover(c, this.bgImg, w, h);
    } else {
      c.fillStyle = "#0B1520"; c.fillRect(0, 0, w, h);
    }
    c.restore();
    this.hasResult = true;
  }

  async _loop() {
    if (!this.running) return;
    if (this.bgType !== "none" && this.seg && this.camVideo && this.camVideo.readyState >= 2) {
      try { await this.seg.send({ image: this.camVideo }); } catch { /* skip frame */ }
    }
    this._render();
    this._raf = requestAnimationFrame(() => this._loop());
  }

  // Draw the person layer, or the raw camera as a fallback (never black).
  _drawPerson(ctx, x, y, w, h) {
    if (this.bgType !== "none" && this.hasResult) {
      ctx.drawImage(this.person, x, y, w, h);
    } else if (this.camVideo && this.camVideo.readyState >= 2) {
      ctx.save();
      ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
      const sw = this.camVideo.videoWidth, sh = this.camVideo.videoHeight;
      if (sw && sh) {
        const r = Math.max(w / sw, h / sh);
        const nw = sw * r, nh = sh * r;
        ctx.drawImage(this.camVideo, x + (w - nw) / 2, y + (h - nh) / 2, nw, nh);
      }
      ctx.restore();
    } else { ctx.fillStyle = "#0B1520"; ctx.fillRect(x, y, w, h); }
  }

  _render() {
    const ctx = this.ctx, w = this.w, h = this.h;
    if (this.mode === "screen" && this.screenVideo && this.screenVideo.readyState >= 2) {
      ctx.fillStyle = "#05090F"; ctx.fillRect(0, 0, w, h);
      drawContain(ctx, this.screenVideo, w, h);
      const pw = Math.round(w * 0.24), ph = Math.round(pw * 9 / 16);
      const x = w - pw - 24, y = h - ph - 24;
      ctx.save();
      roundRect(ctx, x, y, pw, ph, 12); ctx.clip();
      this._drawPerson(ctx, x, y, pw, ph);
      ctx.restore();
      ctx.lineWidth = 3; ctx.strokeStyle = "#FF5F15";
      roundRect(ctx, x, y, pw, ph, 12); ctx.stroke();
    } else {
      this._drawPerson(ctx, 0, 0, w, h);
    }
  }

  setScreen(stream) {
    if (stream) {
      this.screenVideo = document.createElement("video");
      this.screenVideo.muted = true; this.screenVideo.playsInline = true;
      this.screenVideo.srcObject = stream;
      this.screenVideo.play().catch(() => {});
      this.mode = "screen";
    } else {
      if (this.screenVideo) { this.screenVideo.srcObject = null; this.screenVideo = null; }
      this.mode = "camera";
    }
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this._raf);
    try { this.seg?.close(); } catch { /* noop */ }
    if (this.camVideo) this.camVideo.srcObject = null;
    if (this.screenVideo) this.screenVideo.srcObject = null;
  }
}

export const VIDEO_BG_URL = "/video-bg.jpg";
